import { useContext, useEffect, useState } from "react";

import { SocketContext } from "../../context/Socket/SocketContext";
import useSettings from "../useSettings";

export const SIDEBAR_BEHAVIOR_KEY = "sidebarBehavior";
export const SIDEBAR_SUBMENU_BEHAVIOR_KEY = "sidebarSubmenuBehavior";

export const DEFAULT_SIDEBAR_BEHAVIOR = "collapsed";
export const DEFAULT_SIDEBAR_SUBMENU_BEHAVIOR = "collapsible";

export const getCompanyId = () => localStorage.getItem("companyId");

const cacheKey = (key, companyId) => `${key}:${companyId}`;

const readCache = (key, companyId, fallback) => {
  if (!companyId) return fallback;
  return localStorage.getItem(cacheKey(key, companyId)) || fallback;
};

const writeCache = (key, companyId, value) => {
  if (!companyId || !value) return;
  localStorage.setItem(cacheKey(key, companyId), value);
};

/**
 * Le as configuracoes de barra lateral da empresa.
 *
 * O valor inicial vem do localStorage de forma sincrona para que o primeiro
 * render ja use o comportamento correto (evita a barra abrir depois que o
 * GET /settings responde). Depois o valor e confirmado pela API e mantido
 * atualizado pelo socket de settings.
 */
const useSidebarSettings = () => {
  const companyId = getCompanyId();
  const socketManager = useContext(SocketContext);
  const { getAll } = useSettings();

  const [sidebarBehavior, setSidebarBehavior] = useState(() =>
    readCache(SIDEBAR_BEHAVIOR_KEY, companyId, DEFAULT_SIDEBAR_BEHAVIOR)
  );
  const [sidebarSubmenuBehavior, setSidebarSubmenuBehavior] = useState(() =>
    readCache(
      SIDEBAR_SUBMENU_BEHAVIOR_KEY,
      companyId,
      DEFAULT_SIDEBAR_SUBMENU_BEHAVIOR
    )
  );

  useEffect(() => {
    if (!companyId) return undefined;

    let active = true;

    const applySetting = (key, value) => {
      if (!value) return;
      writeCache(key, companyId, value);
      if (key === SIDEBAR_BEHAVIOR_KEY) setSidebarBehavior(value);
      if (key === SIDEBAR_SUBMENU_BEHAVIOR_KEY) setSidebarSubmenuBehavior(value);
    };

    const fetchSettings = async () => {
      try {
        const settings = await getAll();
        if (!active || !Array.isArray(settings)) return;

        [SIDEBAR_BEHAVIOR_KEY, SIDEBAR_SUBMENU_BEHAVIOR_KEY].forEach((key) => {
          const setting = settings.find((s) => s.key === key);
          if (setting) applySetting(key, setting.value);
        });
      } catch (err) {
        // Sem settings continuamos com o valor em cache/default.
      }
    };

    fetchSettings();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    if (!companyId) return undefined;

    // O backend ja coloca a conexao na room `company-<id>-mainchannel`
    // (libs/socket.ts), entao nao ha join a fazer aqui.
    const socket = socketManager.getSocket(companyId);

    const handleSettings = (data) => {
      const setting = data?.setting;
      if (!setting?.key || !setting?.value) return;
      if (
        setting.key !== SIDEBAR_BEHAVIOR_KEY &&
        setting.key !== SIDEBAR_SUBMENU_BEHAVIOR_KEY
      ) {
        return;
      }

      writeCache(setting.key, companyId, setting.value);
      if (setting.key === SIDEBAR_BEHAVIOR_KEY) {
        setSidebarBehavior(setting.value);
      } else {
        setSidebarSubmenuBehavior(setting.value);
      }
    };

    socket.on(`company-${companyId}-settings`, handleSettings);

    return () => {
      socket.off(`company-${companyId}-settings`, handleSettings);
      socket.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, socketManager]);

  return { sidebarBehavior, sidebarSubmenuBehavior };
};

export default useSidebarSettings;
